import type { TFunction } from 'i18next';
import statusConfig from '@/../config/status.json';

/**
 * True once quote generation has completed and an offer exists to act on (the offer
 * page and every later flow route). Single source of truth for the "quotes ready"
 * gate, shared by the header and the flow-guidance hook so the readiness rule can't
 * drift if the status grouping in `status.json` changes.
 */
export function isQuotesComplete(status: string | null | undefined): boolean {
	return status != null && statusConfig.groups.isComplete.includes(status);
}

/**
 * Maps a raw backend status code to the customer-facing i18n key for its label.
 * Single source of truth shared by the quotes header and the request overview so
 * a new status only has to be registered in one place.
 */
export const STATUS_LABEL_I18N_KEYS: Record<string, string> = {
	PENDING_CUSTOMER_EMPLOYEES_INFO: 'quotes.status.pendingWages',
	PENDING_QUOTE_REVIEW: 'quotes.status.pendingSelection',
	CANCELLED: 'quotes.status.cancelled',
	CUSTOMER_APPLIED: 'quotes.status.applicationSubmitted',
	CUSTOMER_SUBMITTED: 'quotes.status.requestInProgress',
	PENDING_CUSTOMER_APPLICATION_SUBMITTED: 'quotes.status.applicationSubmitted',
	PENDING_CUSTOMER_APPLICATION: 'quotes.status.pendingApplication',
	PENDING_CUSTOMER_SELECTION: 'quotes.status.pendingSelection',
	PENDING_CUSTOMER_SUBMISSION: 'quotes.status.pendingApplication',
	PENDING_QUOTES: 'quotes.status.pendingSelection',
	PENDING_CUSTOMER_SECTOR_CODE_ENTRY: 'quotes.status.pendingSectorCode',
	PENDING_CUSTOMER_WAGE_INFO_ENTRY: 'quotes.status.pendingWages',
	PENDING_CUSTOMER_COC_ENTRY: 'quotes.status.pendingCoc',
	PROCESSING_COC_INFO: 'quotes.status.processingCoc',
	PROCESSING_DOCUMENT: 'quotes.status.processingDocument',
	PROCESSING_QUOTES: 'quotes.status.processingQuotes'
};

/**
 * Human-readable label for a backend status: the mapped translation, else the
 * `status.json` description, else null. Callers decide what "no label" means
 * (the header hides the subtitle; the request overview falls back to the code).
 */
export function getStatusLabel(t: TFunction, status: string | null | undefined): string | null {
	if (!status) return null;
	const key = STATUS_LABEL_I18N_KEYS[status];
	if (key) return t(key);
	const description = (statusConfig.descriptions as Record<string, string>)[status];
	return description || null;
}
